// src/hooks/useTemplateTextSelection.js
// Preview sahnesinde raycaster ile text tiklama algilama
import { useCallback, useRef } from 'react';
import * as THREE from 'three';
import { useDispatch } from 'react-redux';
import { selectEntity } from '../../../redux/baslikTemplateSlice';

const HIGHLIGHT_COLOR = 0x4fc3f7;
const ORIGINAL_COLOR_KEY = '_originalColor';

export function useTemplateTextSelection(sceneRef, cameraRef, rendererRef, textEntities) {
  const dispatch = useDispatch();
  const previousHighlightRef = useRef(null);

  const handleClick = useCallback((event) => {
    const scene = sceneRef.current;
    const camera = cameraRef.current;
    const renderer = rendererRef.current;
    if (!scene || !camera || !renderer) return;

    const canvas = renderer.domElement;
    const rect = canvas.getBoundingClientRect();
    const mouse = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );

    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, camera);

    // Sahnedeki tum nesneleri topla
    const intersects = raycaster.intersectObjects(scene.children, true);

    // Onceki highlight'i kaldir
    if (previousHighlightRef.current) {
      const prev = previousHighlightRef.current;
      if (prev.material && prev.userData[ORIGINAL_COLOR_KEY] !== undefined) {
        if (prev.material.color) {
          prev.material.color.setHex(prev.userData[ORIGINAL_COLOR_KEY]);
        }
      }
      previousHighlightRef.current = null;
    }

    // Text/mtext entity bul
    for (const hit of intersects) {
      const obj = hit.object;
      const ud = obj.userData;
      if (ud && (ud.type === 'text' || ud.type === 'mtext' || ud.entityType === 'TEXT' || ud.entityType === 'MTEXT')) {
        // Entity key'i pozisyon esleme ile bul
        const entityKey = findEntityKey(obj, textEntities);
        if (entityKey) {
          // Highlight uygula
          if (obj.material) {
            if (obj.userData[ORIGINAL_COLOR_KEY] === undefined) {
              obj.userData[ORIGINAL_COLOR_KEY] = obj.material.color
                ? obj.material.color.getHex()
                : 0xffffff;
            }
            if (obj.material.color) {
              obj.material.color.setHex(HIGHLIGHT_COLOR);
            }
          }
          previousHighlightRef.current = obj;
          dispatch(selectEntity(entityKey));
          return;
        }
      }
    }

    // Bos alana tiklanirsa secimi kaldir
    dispatch(selectEntity(null));
  }, [sceneRef, cameraRef, rendererRef, textEntities, dispatch]);

  return { handleClick };
}

/**
 * THREE.js objesinin pozisyonunu textEntities listesiyle eslestirir.
 * Entity key dondurur veya null.
 */
function findEntityKey(threeObj, textEntities) {
  if (!textEntities || textEntities.length === 0) return null;

  // userData.entityKey direkt varsa kullan
  if (threeObj.userData?.entityKey) return threeObj.userData.entityKey;

  // Pozisyon esleme: en yakin text entity'yi bul
  const objPos = threeObj.position || new THREE.Vector3();
  let bestKey = null;
  let bestDist = Infinity;

  for (const te of textEntities) {
    const dx = objPos.x - (te.position?.x || 0);
    const dy = objPos.y - (te.position?.y || 0);
    const dist = dx * dx + dy * dy;
    if (dist < bestDist) {
      bestDist = dist;
      bestKey = te.key;
    }
  }

  // Cok uzak ise eslestirme
  if (bestDist > 10000) return null;
  return bestKey;
}
